/**
 * Enhanced Comparison View
 * Displays side-by-side comparison of 30m vs 10m DEM analysis
 */

import React from 'react';
import { Zap, Clock, Cpu } from 'lucide-react';

const EnhancedComparisonView = ({ analysis30m, analysis10m, enhancementMetadata }) => {

  // ── Terrain metric values ──────────────────────────────────────────────
  const terrainMetrics = [
    {
      label: 'Average Slope',
      val30m: `${(analysis30m?.slopeMetrics?.avgSlopeDegrees || 0).toFixed(1)}°`,
      val10m: `${(analysis10m?.slopeMetrics?.avgSlopeDegrees || 0).toFixed(1)}°`,
    },
    {
      label: 'Maximum Slope',
      val30m: `${(analysis30m?.slopeMetrics?.maxSlopeDegrees || 0).toFixed(1)}°`,
      val10m: `${(analysis10m?.slopeMetrics?.maxSlopeDegrees || 0).toFixed(1)}°`,
    },
    {
      label: 'Roughness Index',
      val30m: (analysis30m?.surfaceMetrics?.roughnessIndex || 0).toFixed(1),
      val10m: (analysis10m?.surfaceMetrics?.roughnessIndex || 0).toFixed(1),
    },
  ];

  // ── Physics model component wear values ───────────────────────────────
  const components = [
    {
      label: 'Suspension',
      wear30m:      analysis30m?.componentStress?.suspension?.wearRate          || 0,
      wear10m:      analysis10m?.componentStress?.suspension?.wearRate          || 0,
      calibrated:   analysis10m?.componentStress?.suspension?.wearRate          || 0,
      physics:      analysis10m?.componentStress?.suspensionPhysics?.wearRate   || 0,
      color: 'blue',
    },
    {
      label: 'Brakes',
      wear30m:      analysis30m?.componentStress?.brakes?.wearRate              || 0,
      wear10m:      analysis10m?.componentStress?.brakes?.wearRate              || 0,
      calibrated:   analysis10m?.componentStress?.brakes?.wearRate              || 0,
      physics:      analysis10m?.componentStress?.brakePhysics?.wearRate        || 0,
      color: 'red',
    },
    {
      label: 'Engine',
      wear30m:      analysis30m?.componentStress?.engine?.wearRate              || 0,
      wear10m:      analysis10m?.componentStress?.engine?.wearRate              || 0,
      calibrated:   analysis10m?.componentStress?.engine?.wearRate              || 0,
      physics:      analysis10m?.componentStress?.enginePhysics?.wearRate       || 0,
      color: 'orange',
    },
    {
      label: 'Transmission',
      wear30m:      analysis30m?.componentStress?.transmission?.wearRate        || 0,
      wear10m:      analysis10m?.componentStress?.transmission?.wearRate        || 0,
      calibrated:   analysis10m?.componentStress?.transmission?.wearRate        || 0,
      physics:      analysis10m?.componentStress?.transmissionPhysics?.wearRate || 0,
      color: 'green',
    },
  ];

  const colorMap = {
    blue:   { dot: 'bg-blue-400',   text: 'text-blue-400'   },
    red:    { dot: 'bg-red-400',    text: 'text-red-400'    },
    orange: { dot: 'bg-orange-400', text: 'text-orange-400' },
    green:  { dot: 'bg-green-400',  text: 'text-green-400'  },
  };

  return (
    <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl p-6 shadow-2xl border border-purple-500/30">

      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Zap className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">ML-Enhanced Analysis</h3>
            <p className="text-sm text-gray-400">30m vs 10m Resolution Comparison</p>
          </div>
        </div>
      </div>

      {/* ── Enhancement Metadata ── */}
      {enhancementMetadata && (
        <div className="mb-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="flex items-center gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400">Device:</span>
              <span className="text-white font-semibold">{enhancementMetadata.device || 'Unknown'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400">Enhancement Time:</span>
              <span className="text-white font-semibold">
                {enhancementMetadata.totalEnhancementTime?.toFixed(2) || '0.00'}s
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-purple-400" />
              <span className="text-gray-400">Total Time:</span>
              <span className="text-white font-semibold">
                {enhancementMetadata.totalTime?.toFixed(2) || '0.00'}s
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-8">

        {/* ── Terrain Metrics ── */}
        <div>
          <h4 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
            <span className="w-1 h-4 bg-blue-400 rounded-full inline-block" />
            Terrain Metrics
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {terrainMetrics.map((m, i) => (
              <div key={i} className="bg-gray-800/60 border border-gray-700 rounded-lg p-4">
                <p className="text-xs text-gray-400 font-medium mb-3">{m.label}</p>
                <div className="flex items-center justify-between gap-2">
                  <div className="text-center flex-1">
                    <p className="text-xs text-blue-400 mb-1">30m</p>
                    <p className="text-white font-mono font-bold text-lg">{m.val30m}</p>
                  </div>
                  <div className="text-gray-600 text-lg">→</div>
                  <div className="text-center flex-1">
                    <p className="text-xs text-purple-400 mb-1">10m</p>
                    <p className="text-purple-300 font-mono font-bold text-lg">{m.val10m}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Physics Model Results ── */}
        <div>
          <h4 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
            <span className="w-1 h-4 bg-purple-400 rounded-full inline-block" />
            Physics Model Results
          </h4>
          <p className="text-xs text-gray-500 mb-3">Component wear per 100 km</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {components.map((c, i) => {
              const cls = colorMap[c.color];
              return (
                <div key={i} className="bg-gray-800/60 border border-gray-700 rounded-lg p-4">
                  {/* Component label */}
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-2 h-2 rounded-full ${cls.dot}`} />
                    <p className={`font-semibold text-sm ${cls.text}`}>{c.label}</p>
                  </div>

                  {/* 4-value grid */}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="bg-gray-900/60 rounded-md p-2.5 text-center">
                      <p className="text-xs text-blue-400 mb-1">30m Resolution</p>
                      <p className="text-white font-mono font-bold">{c.wear30m.toFixed(3)}%</p>
                    </div>
                    <div className="bg-gray-900/60 rounded-md p-2.5 text-center">
                      <p className="text-xs text-green-400 mb-1">10m Resolution</p>
                      <p className="text-green-300 font-mono font-bold">{c.wear10m.toFixed(3)}%</p>
                    </div>
                    <div className="bg-gray-900/60 rounded-md p-2.5 text-center">
                      <p className="text-xs text-yellow-400 mb-1">Calibrated Model</p>
                      <p className="text-yellow-300 font-mono font-bold">{c.calibrated.toFixed(3)}%</p>
                      <p className="text-gray-500 text-xs mt-0.5">Empirical</p>
                    </div>
                    <div className="bg-gray-900/60 rounded-md p-2.5 text-center">
                      <p className="text-xs text-indigo-400 mb-1">Physics Model</p>
                      <p className="text-indigo-300 font-mono font-bold">{c.physics.toFixed(3)}%</p>
                      <p className="text-gray-500 text-xs mt-0.5">Quarter-Car</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
};

export default EnhancedComparisonView;
